/**
 * Substratwechsel: welche Zeilen liegen miteinander im Wechsel?
 *
 * Regel (Nutzer-Entscheidung 18.09.2026): Der Haken an einer Substratzeile
 * sagt „diese Schicht wechselt mit der Schicht DARUNTER". Mehrere Haken
 * hintereinander ergeben eine Kette; die letzte Zeile der Kette traegt selbst
 * keinen Haken mehr, gehoert aber dazu.
 *
 *     Tiefe  W   Substrat
 *      20    ·   Sl2
 *      45    x   mS       ┐ wechselt mit
 *      60    x   Ut3      ┤ wechselt mit
 *      80    ·   T        ┘
 *      90    ·   S
 *
 * Bis 18.09.2026 sagte das allein der Eintrag in der Spalte; welche Zeilen
 * zusammengehoeren, musste man sich selbst zusammenreimen. Die Lagen hier
 * ergeben die Klammer daneben — in der Erfassung, in der Uebersicht und im
 * gedruckten Protokoll. EINE Regel fuer alle drei: Wer sie zweimal baut,
 * bekommt zwei Fassungen, die auseinanderlaufen.
 *
 * Ein Haken an der LETZTEN Zeile verbindet mit nichts — es gibt keine Zeile
 * darunter. Dann entsteht keine Klammer; der Haken selbst bleibt sichtbar und
 * sagt, dass die Angabe unvollstaendig ist. Stillschweigend eine Kette zu
 * erfinden hiesse raten.
 */

/** Wo steht eine Zeile in ihrer Wechsel-Kette? */
export type WechselLage = 'keine' | 'anfang' | 'mitte' | 'ende';

/** Alles, was fuer die Regel zaehlt: der Haken der Zeile. */
export interface ZeileMitWechsel {
	wechselschichtung?: boolean;
}

/**
 * Lage EINER Substratzeile. Schaut nur auf sich und den Nachbarn darueber —
 * die Vorlage ruft das je Zeile bei jedem Durchlauf auf, da darf nicht jedes
 * Mal die ganze Liste durchgerechnet werden.
 */
export function wechselLage(zeilen: readonly ZeileMitWechsel[] | null | undefined, i: number): WechselLage {
	const liste = zeilen ?? [];
	// Verbindet Zeile i mit der Zeile darunter? Nur, wenn es eine gibt.
	const verbindet = (k: number) => k >= 0 && k + 1 < liste.length && !!liste[k]?.wechselschichtung;

	const nachOben = verbindet(i - 1);
	const nachUnten = verbindet(i);
	if (nachOben && nachUnten) return 'mitte';
	if (nachUnten) return 'anfang';
	if (nachOben) return 'ende';
	return 'keine';
}

/**
 * Lage jeder Substratzeile in ihrer Wechsel-Kette, in der Reihenfolge der
 * Zeilen.
 */
export function wechselLagen(zeilen: readonly ZeileMitWechsel[] | null | undefined): WechselLage[] {
	return (zeilen ?? []).map((_, i) => wechselLage(zeilen, i));
}

/**
 * Dieselben Lagen fuer eine Ansicht, in der zwischen zwei Substratzeilen
 * Zeilen OHNE Substrat stehen koennen (die Uebersicht fuehrt Substrat- und
 * Horizontfolge nebeneinander und richtet sie nach der Tiefe aus).
 *
 * `substratIndizes` nennt je Anzeigezeile die Substratzeile, die dort steht,
 * oder `null`. Eine Zeile ohne Substrat zieht die Linie durch, solange die
 * Kette noch laeuft — sonst risse die Klammer mitten in einer Gruppe auf.
 */
export function wechselLagenFuerAnzeige(
	zeilen: readonly ZeileMitWechsel[] | null | undefined,
	substratIndizes: readonly (number | null)[],
): WechselLage[] {
	const lagen = wechselLagen(zeilen);
	let kettelaeuft = false;

	return substratIndizes.map(index => {
		if (index === null || index === undefined) return kettelaeuft ? 'mitte' : 'keine';
		const lage = lagen[index] ?? 'keine';
		kettelaeuft = lage === 'anfang' || lage === 'mitte';
		return lage;
	});
}

/** Gehoert die Zeile ueberhaupt zu einer Kette? */
export function imWechsel(lage: WechselLage): boolean {
	return lage !== 'keine';
}
